import { Controller } from "@hotwired/stimulus"
import { createHereMap, disposeHereMap } from "../helpers/here_map_helpers"
import { attachHereAutocomplete, hereGeocode } from "../helpers/here_places"

const GERMANY_CENTER = { lat: 51.165691, lng: 10.451526 }

export default class extends Controller {
  static targets = ["field", "map", "latitude", "longitude", "customLocation", "country"]
  // The country searched when no Land is selected (the tenant's own; for an additional
  // site location the site's effective Land). Mirrors Site#geocoding_country.
  static values = { defaultCountry: String }

  connect() {
    // A tenant's form layout can hide the address block; the controller sits on the whole
    // form, so its targets are then simply absent.
    if (!this.hasMapTarget || !this.hasLatitudeTarget || !this.hasLongitudeTarget) return

    const hasCoords = this.latitudeTarget.value && this.longitudeTarget.value
    createHereMap(this.mapTarget, {
      center: hasCoords
        ? {
            lat: parseFloat(this.latitudeTarget.value),
            lng: parseFloat(this.longitudeTarget.value),
          }
        : GERMANY_CENTER,
      zoom: hasCoords ? 15 : 5,
    })
      .then((ctx) => this.initializeMap(ctx))
      .catch((error) => console.error("HERE map failed to load", error))
  }

  disconnect() {
    if (this.teardownAutocomplete) this.teardownAutocomplete()
    // The form lives in an offcanvas and nested-form rows come and go — release the engine.
    disposeHereMap(this.map)
    this.map = null
  }

  initializeMap({ H, map, behavior }) {
    this.H = H
    this.map = map
    this.behavior = behavior

    if (this.latitudeTarget.value && this.longitudeTarget.value) {
      this.setMarker({
        lat: parseFloat(this.latitudeTarget.value),
        lng: parseFloat(this.longitudeTarget.value),
      })
    }

    this.setupAutocomplete()
    this.setupMarkerDragging()
    this.geocodeInitialAddress()

    // Allow dropping/moving the pin by clicking anywhere on the map — useful for
    // precise spots on large sites (gates, access points) that have no street address.
    map.addEventListener("tap", (event) => this.mapClicked(event))
  }

  mapClicked(event) {
    // Ignore taps that land on the marker itself (those start a drag instead).
    if (event.target === this._marker) return
    const coord = this.map.screenToGeo(
      event.currentPointer.viewportX,
      event.currentPointer.viewportY,
    )
    this.setMarker({ lat: coord.lat, lng: coord.lng })
    this.updateLatLong(coord.lat, coord.lng)
    if (this.hasCustomLocationTarget) this.customLocationTarget.value = true
  }

  setupAutocomplete() {
    this.teardownAutocomplete = attachHereAutocomplete(this.fieldTarget, {
      country: () => this.selectedCountry(),
      onSelect: (fields) => {
        // Write the picked address back into the input: attachHereAutocomplete only
        // reports the selection, each consumer decides what to store. Without this the
        // field keeps the half-typed query while the pin sits on the real address, and
        // that query is what gets saved (and later re-geocoded server-side).
        this.fieldTarget.value = fields.label || fields.street || this.fieldTarget.value
        this.applyCountry(fields.country)
        if (!fields.position) return

        this.centerOn(fields.position)
        this.updateLatLong(fields.position.lat, fields.position.lng)
        if (this.hasCustomLocationTarget) this.customLocationTarget.value = false
      },
    })
  }

  // HERE markers are not draggable out of the box: disable map panning while a marker
  // drag is in progress and follow the pointer manually (HERE's documented pattern).
  setupMarkerDragging() {
    this.map.addEventListener("dragstart", (event) => {
      if (event.target === this._marker) this.behavior.disable()
    })
    this.map.addEventListener("drag", (event) => {
      if (event.target !== this._marker) return
      const pointer = event.currentPointer
      this._marker.setGeometry(this.map.screenToGeo(pointer.viewportX, pointer.viewportY))
    })
    this.map.addEventListener("dragend", (event) => {
      if (event.target !== this._marker) return
      this.behavior.enable()
      const geo = this._marker.getGeometry()
      this.updateLatLong(geo.lat, geo.lng)
      if (this.hasCustomLocationTarget) this.customLocationTarget.value = true
    })
  }

  geocodeInitialAddress() {
    if (this.latitudeTarget.value && this.longitudeTarget.value) return

    const address = this.hasFieldTarget ? this.fieldTarget.value.trim() : ""
    if (address) this.geocodeAddress(address)
  }

  // An address picked from the customer address book. Its country (when the book
  // knows one) becomes the form's Land first, so the geocode below searches there.
  geocodeAndCenter(event) {
    const address = event.detail?.address
    if (!address || !this.map) return

    if (event.detail.country) this.applyCountry(event.detail.country)
    this.geocodeAddress(address)
  }

  async geocodeAddress(address) {
    try {
      const fields = await hereGeocode(address, this.selectedCountry())
      if (!fields?.position) return
      this.centerOn(fields.position)
      this.updateLatLong(fields.position.lat, fields.position.lng)
      if (this.hasCustomLocationTarget) this.customLocationTarget.value = false
    } catch (error) {
      // the operator can still place the pin manually, but don't fail silently
      console.error("HERE geocoding failed", error)
    }
  }

  // Snap to the picked address instead of animating: the fly from country zoom (5) to
  // street zoom (15) across the map read as multi-second lag. A single setLookAtData call
  // (not setCenter + setZoom) still avoids the marker/centre desync that two separate
  // animated calls caused — the second one cancelled the first and left the map on its
  // old centre while the marker jumped ahead.
  centerOn(position) {
    this.map.getViewModel().setLookAtData({ position, zoom: 15 }, false)
    this.setMarker(position)
  }

  setMarker(position) {
    if (!this._marker) {
      this._marker = new this.H.map.Marker(position, { volatility: true })
      this._marker.draggable = true
      this.map.addObject(this._marker)
    } else {
      this._marker.setGeometry(position)
    }
  }

  selectedCountry() {
    const selected = this.hasCountryTarget ? this.countryTarget.value : ""
    return selected || this.defaultCountryValue || null
  }

  // Mirror the picked address's country into the Land select. A country the select does
  // not offer resets it to the blank "Land der Firma" option: a stale Land would pin the
  // server-side geocode to the wrong country.
  applyCountry(alpha2) {
    if (!this.hasCountryTarget) return

    const code = (alpha2 || "").toLowerCase()
    const known = Array.from(this.countryTarget.options).some((option) => option.value === code)
    const value = known ? code : ""
    if (this.countryTarget.value === value) return

    const select = this.application.getControllerForElementAndIdentifier(
      this.countryTarget,
      "select",
    )
    if (select) {
      select.setSelected(value)
    } else {
      this.countryTarget.value = value
    }
  }

  updateLatLong(lat, long) {
    this.latitudeTarget.value = lat
    this.longitudeTarget.value = long
  }

  preventSubmit(e) {
    if (e.key == "Enter") {
      e.preventDefault()
    }
  }
}
